import { ElectronApi } from '@/services/electron.api';

/**
 * 检查文件或路径是否存在
 * @param path
 * @returns
 */
export const testAccess = async (path: string) => {
  try {
    const fs = ElectronApi.fs;
    await fs?.promises.access(path, fs.constants.F_OK);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * 返回目录类型
 * @param path
 * @param type
 * @returns
 */
const _getType = async (
  filename: string
): Promise<'directory' | 'file' | undefined> => {
  const S_IFMT = 61440; /* 0170000 type of file */
  const S_IFDIR = 16384; /* 0040000 directory */
  const S_IFREG = 32768; /* 0100000 regular */

  const fs = ElectronApi.fs;
  try {
    const { mode = -1 } = (await fs?.promises.stat(filename)) ?? {};
    const res = mode & S_IFMT;
    if (res === S_IFDIR) return 'directory';
    if (res === S_IFREG) return 'file';
  } catch (error) {
    return;
  }
};

/**
 * 判断路径是否为目录
 */
export const isDirectory = async (path: string) => {
  return (await _getType(path)) == 'directory';
};

/**
 * 判断路径是否为文件
 */
export const isFile = async (path: string) => {
  return (await _getType(path)) == 'file';
};

export const ensureDir = async (dir: string) => {
  const fs = ElectronApi.fs;
  if (!fs) return;
  if (await testAccess(dir)) return;
  await fs.promises.mkdir(dir, { recursive: true });
};

export const emptyDir = async (dir: string) => {
  const fs = ElectronApi.fs;
  const path = ElectronApi.path;
  if (!fs || !path) return;
  if (!(await isDirectory(dir))) return;

  const files = await fs.promises.readdir(dir);
  for (const _file of files) {
    const _filename = path.join(dir, _file);
    await fs.promises.rm(_filename, { recursive: true, force: true });
  }
};

export const isEmptyDir = async (dir: string): Promise<boolean> => {
  const fs = ElectronApi.fs;
  const path = ElectronApi.path;
  if (!fs || !path) return false;
  if (!(await isDirectory(dir))) return false;

  const files = await fs.promises.readdir(dir);
  return files.length === 0;
};

export const fsReadDir = async (dir: string) => {
  const fs = ElectronApi.fs;
  const path = ElectronApi.path;
  if (!fs || !path) return;

  try {
    const res: { realpath: string; basename: string }[] = [];
    const stack: {
      dir: string;
      parentBasename?: string;
    }[] = [{ dir }];
    while (stack.length) {
      const { dir: _dir, parentBasename } = stack.shift() ?? {};
      if (!_dir) continue;
      const filenames: string[] = await fs.promises.readdir(_dir);
      for (const name of filenames) {
        const basename = parentBasename ? parentBasename + '/' + name : name;
        const _filename = path.join(_dir, name);
        if (await isFile(_filename)) {
          res.push({
            realpath: _filename,
            basename
          });
        }
        if (await isDirectory(_filename)) {
          stack.push({ dir: _filename, parentBasename: basename });
        }
      }
    }
    return res;
  } catch (error) {
    return [];
  }
};
